<template>
  <ErrorModal />
  <DxScrollView width="100%" class="edit-user">
    <div class="px-3 pt-3 pr-3">
      <div style="display: flex; justify-content: space-between">
        <h2 class="title bold-font">
          {{ t("account_management.edit_user") }}
        </h2>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.first_name") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="firstName"
            :placeholder="t('account_management.first_name')"
            :input-attr="{
              'aria-label': t('account_management.first_name'),
            }"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'editUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("profile.last_name") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="lastName"
            :placeholder="t('profile.last_name')"
            :input-attr="{
              'aria-label': t('profile.last_name'),
            }"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'editUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">{{ t("account_management.e_mail") }}</div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="email"
            :input-attr="{ 'aria-label': t('account_management.e_mail') }"
            :placeholder="t('account_management.e_mail')"
            @focusOut="focusOutFn()"
          >
            <DxValidator :validation-group="'editUserValidationGroup'">
              <DxRequiredRule :message="t('common.required_field')" />
              <DxEmailRule :message="t('common.invalid_email')" />
            </DxValidator>
          </DxTextBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.start_date") }}
        </div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="startDate"
            :input-attr="{ 'aria-label': t('account_management.start_date') }"
            :placeholder="t('account_management.start_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">{{ t("account_management.end_date") }}</div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="endDate"
            :input-attr="{ 'aria-label': t('account_management.end_date') }"
            :placeholder="t('account_management.end_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>

      <div class="dx-field" v-if="customerHasSas">
        <div class="dx-field-label">
          {{ t("account_management.sas_start_date") }}
        </div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="sasStartDate"
            :input-attr="{
              'aria-label': t('account_management.sas_start_date'),
            }"
            :placeholder="t('account_management.sas_start_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>

      <div class="dx-field" v-if="customerHasSas">
        <div class="dx-field-label">
          {{ t("account_management.sas_end_date") }}
        </div>
        <div class="dx-field-value">
          <DxDateBox
            v-model:value="sasEndDate"
            :input-attr="{ 'aria-label': t('account_management.sas_end_date') }"
            :placeholder="t('account_management.sas_end_date')"
            type="date"
            display-format="dd-MM-yyyy"
            date-serialization-format="yyyy-MM-ddTHH:mm:ss"
            :open-on-field-click="true"
          >
          </DxDateBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.department") }}
        </div>
        <div class="dx-field-value">
          <DxTextBox
            v-model:value="department"
            :input-attr="{
              'aria-label': t('account_management.department'),
            }"
            :placeholder="t('account_management.department')"
            @focusOut="focusOutFn()"
          >
          </DxTextBox>
        </div>
      </div>

      <div class="dx-field">
        <div class="dx-field-label">
          {{ t("account_management.content_roles") }}
        </div>
        <div class="dx-field-value">
          <DxTagBox
            :items="contentRoles"
            display-expr="name"
            value-expr="id"
            v-model:value="selectedContentRolesIds"
            :placeholder="t('common.select_placeholder')"
            :input-attr="{
              'aria-label': t('account_management.content_roles'),
            }"
            apply-value-mode="instantly"
            :show-selection-controls="true"
          >
          </DxTagBox>
        </div>
      </div>

      <div class="dx-field" v-if="customerHasSas">
        <div class="dx-field-label">
          {{ t("account_management.sas_roles") }}
        </div>
        <div class="dx-field-value">
          <DxTagBox
            :items="sasRoles"
            display-expr="name"
            value-expr="id"
            v-model:value="selectedSasRolesIds"
            :placeholder="t('common.select_placeholder')"
            :input-attr="{
              'aria-label': t('account_management.sas_roles'),
            }"
            apply-value-mode="instantly"
            :show-selection-controls="true"
          >
          </DxTagBox>
        </div>
      </div>

      <div class="dx-field" v-if="isSuperAdmin">
        <div class="dx-field-label">
          {{ t("account_management.account_manager") }}
        </div>
        <div class="dx-field-value" style="display: flex">
          <DxCheckBox
            v-model:value="isAccountManager"
            :placeholder="t('account_management.account_manager')"
            :input-attr="{
              'aria-label': t('account_management.account_manager'),
            }"
            :hint="t('account_management.account_manager')"
          >
          </DxCheckBox>
        </div>
      </div>
      <div class="dx-field" v-if="customerHasSas">
        <div class="dx-field-label">
          {{ t("account_management.receives_sas_task_emails") }}
        </div>
        <div class="dx-field-value-checkbox">
          <DxCheckBox
            v-model:value="hasSasTaskEmails"
            :input-attr="{
              'aria-label': t('account_management.receives_sas_task_emails'),
            }"
            :hint="t('account_management.receives_sas_task_emails')"
          />
        </div>
      </div>
      <div
        class="mt-4 btns-row"
        style="display: flex; justify-content: space-evenly"
      >
        <DxButton
          :text="t('common.save')"
          class="orange-btn-filled"
          @click="editUser"
          :disabled="isSaveButtonDisabled"
        />
        <DxButton
          :text="t('common.cancel')"
          class="orange-btn bg-gray-button"
          @click="showWarningModal()"
        />
      </div>
    </div>
  </DxScrollView>
  <WarningModal :onOk="handleOk" v-if="warningModalVisible" />
</template>
<script>
import { computed, onBeforeUnmount, ref, onMounted } from "vue";

import { DxScrollView } from "devextreme-vue/scroll-view";
import { DxButton } from "devextreme-vue/button";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxTagBox } from "devextreme-vue/tag-box";
import { DxCheckBox } from "devextreme-vue/check-box";
import { DxDateBox } from "devextreme-vue/date-box";
import {
  DxValidator,
  DxRequiredRule,
  DxEmailRule,
} from "devextreme-vue/validator";
import validationEngine from "devextreme/ui/validation_engine";

import { useI18n } from "vue-i18n";
import WarningModal from "../global-components/WarningModal.vue";
import ErrorModal from "../../views/ErrorModal.vue";
import UsersService from "../../services/userprofile.service";
import store from "../../store";

export default {
  components: {
    WarningModal,
    DxScrollView,
    DxButton,
    DxTextBox,
    DxTagBox,
    DxCheckBox,
    DxDateBox,
    DxValidator,
    DxRequiredRule,
    DxEmailRule,
    ErrorModal,
  },
  props: {
    onSave: Function,
  },
  setup(props) {
    const { t, locale } = useI18n();
    const isDirty = ref(false);

    const customerHasSas = computed(
      () => store.state.auth.authUser.customerHasSas
    );
    const isSuperAdmin = computed(() => store.state.auth.authUser.isSuperAdmin);

    const validationGroupName = "editUserValidationGroup";
    const isSaveButtonDisabled = ref(false);

    onMounted(async () => {
      await UsersService.getContentRoles();
      await UsersService.getSasRoles(false);
    });

    const userId = computed({
      get: () => store.state.user.selectedUserForEdit.id,
    });
    const email = computed({
      get: () => store.state.user.selectedUserForEdit.email,
      set: (value) => {
        store.commit("user/updateSelectedUserEmail", value);
        isDirty.value = true;
      },
    });
    const firstName = computed({
      get: () => store.state.user.selectedUserForEdit.firstName,
      set: (value) => {
        store.commit("user/updateSelectedUserFirstName", value);
        isDirty.value = true;
      },
    });
    const lastName = computed({
      get: () => store.state.user.selectedUserForEdit.lastName,
      set: (value) => {
        store.commit("user/updateSelectedUserLastName", value);
        isDirty.value = true;
      },
    });

    const isAccountManager = computed({
      get: () => store.state.user.selectedUserForEdit.isAccountManager,
      set: (value) => {
        store.commit("user/updateSelectedUserIsAccountManager", value);
        isDirty.value = true;
      },
    });
    const department = computed({
      get: () => store.state.user.selectedUserForEdit.department,
      set: (value) => {
        store.commit("user/updateSelectedUserDepartment", value);
        isDirty.value = true;
      },
    });

    const startDate = computed({
      get: () => store.state.user.selectedUserForEdit.startDate,
      set: (value) => {
        store.commit("user/updateSelectedUserStartDate", value);
        isDirty.value = true;
      },
    });
    const endDate = computed({
      get: () => store.state.user.selectedUserForEdit.endDate,
      set: (value) => {
        store.commit("user/updateSelectedUserEndDate", value);
        isDirty.value = true;
      },
    });
    const sasStartDate = computed({
      get: () => store.state.user.selectedUserForEdit.sasStartDate,
      set: (value) => {
        store.commit("user/updateSelectedUserSasStartDate", value);
        isDirty.value = true;
      },
    });
    const sasEndDate = computed({
      get: () => store.state.user.selectedUserForEdit.sasEndDate,
      set: (value) => {
        store.commit("user/updateSelectedUserSasEndDate", value);
        isDirty.value = true;
      },
    });
    const selectedContentRolesIds = computed({
      get: () => store.state.user.selectedUserForEdit.selectedContentRolesIds,
      set: (value) => {
        store.commit("user/updateSelectedUserContentRolesIds", value);
        isDirty.value = true;
      },
    });
    const selectedSasRolesIds = computed({
      get: () => store.state.user.selectedUserForEdit.selectedSasRolesIds,
      set: (value) => {
        store.commit("user/updateSelectedUserSasRolesIds", value);
        isDirty.value = true;
      },
    });
    const hasSasTaskEmails = computed({
      get: () => store.state.user.selectedUserForEdit.hasSasTaskEmails,
      set: (value) => {
        store.commit("user/updateSelectedUserHasSasTaskEmails", value);
        isDirty.value = true;
      },
    });
    const salutation = computed({
      get: () => store.state.user.selectedUserForEdit.salutation,
    });

    const warningModalVisible = computed(
      () => store.state.warningModal.warningModalVisible
    );

    const sasRoles = computed(() => store.state.user.sasRoles);
    const contentRoles = computed(() => store.state.user.contentRoles);

    const handleBeforeUnload = (event) => {
      if (isDirty.value) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    onBeforeUnmount(() => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    });

    const updatedUser = {
      id: userId,
      email: email,
      firstName: firstName,
      lastName: lastName,
      department: department,
      isAccountManager: isAccountManager,
      salutation: salutation,
      startDate: startDate,
      endDate: endDate,
      sasStartDate: sasStartDate,
      sasEndDate: sasEndDate,
      selectedContentRolesIds: selectedContentRolesIds,
      selectedSasRolesIds: selectedSasRolesIds,
      hasSasTaskEmails: hasSasTaskEmails,
    };

    const editUser = async () => {
      await UsersService.editFrontOfficeUser(updatedUser, locale).then(
        (response) => {
          if (response.status === 200) {
            store.commit("infoModal/openInfoModal");
            store.commit(
              "infoModal/setInfoModalText",
              t("account_management.user_successfully_updated")
            );
          }
        }
      );

      closeEditUserDrawer();
      props.onSave();
    };

    const closeEditUserDrawer = () => {
      store.commit("user/disableEditUser");
    };

    const showWarningModal = () => {
      if (isDirty.value) {
        store.commit(
          "warningModal/setWarningModalTitle",
          t("common.unsaved_changes_popup_title")
        );

        store.commit(
          "warningModal/setWarningModalText",
          t("common.warning_message")
        );

        store.commit("warningModal/openWarningModal");

        store.commit(
          "warningModal/setWarningModalTooltipText",
          t("common.warning_yes_tooltip_message"),
          t("common.warning_no_tooltip_message")
        );
      } else {
        closeEditUserDrawer();
      }
    };
    const handleOk = () => {
      store.commit("warningModal/closeWarningModal");
      closeEditUserDrawer();
    };

    const focusOutFn = () => {
      validateForm();
    };

    const validateForm = () => {
      const result = validationEngine.validateGroup(validationGroupName);
      if (result) {
        isSaveButtonDisabled.value = !result.isValid;
      } else {
        isSaveButtonDisabled.value = true;
      }
      return result?.isValid || false;
    };

    return {
      editUser,
      updatedUser,
      customerHasSas,
      isSuperAdmin,
      closeEditUserDrawer,
      showWarningModal,
      handleOk,
      handleBeforeUnload,
      sasRoles,
      contentRoles,
      warningModalVisible,
      salutation,
      userId,
      firstName,
      lastName,
      email,
      department,
      isAccountManager,
      hasSasTaskEmails,
      selectedContentRolesIds,
      selectedSasRolesIds,
      sasEndDate,
      sasStartDate,
      startDate,
      endDate,
      focusOutFn,
      validateForm,
      isSaveButtonDisabled,
    };
  },
};
</script>
<style lang="scss" scoped>
.edit-user {
  width: 600px !important;

  .title {
    color: #0a108f;
    font-size: 18px;
    margin-left: 10px;
  }
  .dx-field {
    margin: 10px !important;

    .dx-field-label {
      font-family: "ProximaNovaBold";
      color: #0a108f;
      text-align: left;
    }
  }
  .dx-field {
    width: 80%;
    margin-left: 3% !important;
  }
  .btns-row {
    width: 50%;
    margin: auto;
  }
}

@media screen and (max-width: 1028px) {
  .edit-user {
    width: 300px !important;
  }
  .btns-row {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
}
</style>
